/**
 * Copyright © 2026 Adnuntius AS.
 */

// Matches Angular's DecimalPipe/PercentPipe digitsInfo grammar:
// {minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}
const DIGITS_INFO_PATTERN = /^(\d+)?\.((\d+)(-(\d+))?)?$/;

interface DigitsInfoOptions {
  minimumIntegerDigits: number;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
}

function parseDigitsInfo(digitsInfo: string | undefined, defaultMinFraction: number, defaultMaxFraction: number): DigitsInfoOptions {
  const options: DigitsInfoOptions = {
    minimumIntegerDigits: 1,
    minimumFractionDigits: defaultMinFraction,
    maximumFractionDigits: defaultMaxFraction
  };
  if (!digitsInfo) {
    return options;
  }
  const match = digitsInfo.match(DIGITS_INFO_PATTERN);
  if (!match) {
    return options;
  }
  if (match[1] !== undefined) {
    options.minimumIntegerDigits = parseInt(match[1], 10);
  }
  if (match[3] !== undefined) {
    options.minimumFractionDigits = parseInt(match[3], 10);
  }
  if (match[5] !== undefined) {
    options.maximumFractionDigits = parseInt(match[5], 10);
  } else if (match[3] !== undefined) {
    options.maximumFractionDigits = Math.max(options.minimumFractionDigits, defaultMaxFraction);
  }
  if (options.maximumFractionDigits < options.minimumFractionDigits) {
    options.maximumFractionDigits = options.minimumFractionDigits;
  }
  return options;
}

export function formatLocaleNumber(value: number | string, digitsInfo: string | undefined, locale: string): string | null {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) {
    return null;
  }
  const options = parseDigitsInfo(digitsInfo, 0, 3);
  return new Intl.NumberFormat(locale, options).format(num);
}

export function formatLocalePercent(value: number | string, digitsInfo: string | undefined, locale: string): string | null {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) {
    return null;
  }
  const options = parseDigitsInfo(digitsInfo, 0, 0);
  return new Intl.NumberFormat(locale, {...options, style: 'percent'}).format(num);
}
